import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import type { AuthResponse, UserProfile } from '@/types/api'

const API_BASE = import.meta.env.VITE_API_URL || ''

export function useAuth() {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<UserProfile | null>(() => {
    const stored = localStorage.getItem('user')
    if (stored) {
      const parsed = JSON.parse(stored)
      Sentry.setUser({ id: parsed.id, email: parsed.email, org_id: parsed.organization_id ?? undefined })
      posthog.identify(parsed.id, {
        email: parsed.email,
        name: parsed.name,
        org_id: parsed.organization_id,
        role: parsed.role,
      })
      return parsed
    }
    return null
  })

  const isAuthenticated = !!localStorage.getItem('access_token')

  const login = useCallback(async (email: string, password: string) => {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Login failed')
    }

    const data: AuthResponse = await response.json()
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)
    localStorage.setItem('user', JSON.stringify(data.user))
    setUser(data.user)
    Sentry.setUser({ id: data.user.id, email: data.user.email, org_id: data.user.organization_id ?? undefined })
    posthog.identify(data.user.id, {
      email: data.user.email,
      name: data.user.name,
      org_id: data.user.organization_id,
      role: data.user.role,
    })
    return data
  }, [])

  const register = useCallback(async (email: string, password: string, name?: string, organization_name?: string) => {
    const response = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, organization_name }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Registration failed')
    }

    const data: AuthResponse = await response.json()
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)
    localStorage.setItem('user', JSON.stringify(data.user))
    setUser(data.user)
    Sentry.setUser({ id: data.user.id, email: data.user.email, org_id: data.user.organization_id ?? undefined })
    posthog.identify(data.user.id, {
      email: data.user.email,
      name: data.user.name,
      org_id: data.user.organization_id,
      role: data.user.role,
    })
    return data
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    setUser(null)
    Sentry.setUser(null)
    posthog.reset()
    queryClient.clear()
  }, [queryClient])

  return { user, isAuthenticated, login, register, logout }
}
